// @flow strict

import React from 'react';
import { useLanguageContext } from '~contexts/language-context';

import { footerData, socialLinks, ratingBadges } from './footer-data';

import * as styles from './footer.module.less';

/**
 * Footer of the page.
 *
 * Mirrors the main website footer (`_includes/components/footer.html`):
 * brand/newsletter column, company links, product links, articles and a
 * bottom bar with the EU funding note, logo and copyright.
 *
 * @returns {Node}
 */
export const Footer = () => {
  const { language } = useLanguageContext();
  const data = footerData[language] || footerData.en;
  const year = new Date().getFullYear();

  return (
    <footer className={styles.footer}>
      <div className={styles.inner}>
        <div className={styles.columns}>
          <div className={styles.brandColumn}>
            <div>
              <span className={styles.brandHeadline}>
                Power Up Your
                <br />
                Customer Service
              </span>
              <div className={styles.socialMediaList}>
                {socialLinks.map((social) => (
                  <a
                    key={social.href}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={social.title}
                    className={styles.socialMediaItem}
                  >
                    <img src={social.imgSrc} alt={social.alt} />
                  </a>
                ))}
              </div>
            </div>

            <a
              href={data.newsletterUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.newsletter}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 22 22"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.4256 2.57793H18.1349V0H3.86868V2.57793H2.57793V6.52169L0 9.42142V22H22V9.42142L19.4221 6.52169V2.57793H19.4256ZM6.3322 15.4962L1.29433 19.9298V11.0876L6.3322 15.4962ZM11.0018 13.1042L19.6473 20.7092H2.35267L10.9982 13.1042H11.0018ZM15.675 15.4962L20.7128 11.0876V19.9298L15.675 15.4962ZM5.15943 1.29075H16.8477V2.58151H5.15943V1.29075ZM18.1349 3.86867V11.6346L14.6988 14.6416L11.0018 11.3915L7.30473 14.6416L3.86868 11.6346V3.86867H18.1349ZM2.58151 10.5048L1.56249 9.61092L2.58151 8.46318V10.5048ZM20.4447 9.61092L19.4256 10.5048V8.46318L20.4447 9.61092Z"
                  fill="black"
                />
                <path
                  d="M16.8445 5.15625H5.15625V6.44701H16.8445V5.15625Z"
                  fill="black"
                />
                <path
                  d="M16.8445 7.7334H5.15625V9.02415H16.8445V7.7334Z"
                  fill="black"
                />
                <path
                  d="M9.06784 10.3125H5.15625V11.6032H9.06784V10.3125Z"
                  fill="black"
                />
              </svg>
              {data.newsletterLabel}
            </a>

            <div className={styles.ratings}>
              {ratingBadges.map((badge) => (
                <img key={badge.imgSrc} src={badge.imgSrc} alt={badge.alt} />
              ))}
            </div>
          </div>

          <div className={styles.linkColumn}>
            <span className={styles.columnTitle}>{data.companyTitle}</span>
            {data.company.map((item) => (
              <a key={item.href} href={item.href} className={styles.link}>
                {item.title}
              </a>
            ))}
          </div>

          <div className={styles.linkColumn}>
            <span className={styles.columnTitle}>{data.productsTitle}</span>
            {data.products.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`${styles.link} ${styles.productLink}`}
              >
                <img src={item.imgSrc} alt={item.alt} />
                {item.title}
              </a>
            ))}
          </div>

          <div className={`${styles.linkColumn} ${styles.articleColumn}`}>
            <span className={styles.columnTitle}>{data.articleTitle}</span>
            {data.articles.map((item) => (
              <a key={item.href} href={item.href} className={styles.link}>
                {item.title}
              </a>
            ))}
          </div>
        </div>

        <div className={styles.bottomBar}>
          <a href={data.homeUrl} className={styles.bottomLogo} title="OMQ">
            <img src="/images/menu/omq-black.svg" alt="OMQ Logo" />
          </a>
          <div className={styles.euFunding}>
            <img
              src="/images/misc/eu-european-union-flag.png"
              alt="European Union Flag"
            />
            <span>{data.euFunding}</span>
          </div>
          <span className={styles.copyright}>
            © Copyright {year} OMQ. {data.copyrightSuffix}
          </span>
        </div>
      </div>
    </footer>
  );
};
